import React, { useEffect, useState, useCallback } from 'react';
import API from '../api';
import { Archive, RotateCcw, Trash2, FolderOpen, FileText, Ruler, HardHat, Users, Truck, Receipt } from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';

const ENTITIES = [
  { key: 'pratiche', label: 'Pratiche', icon: FolderOpen, fields: ['codice_pratica', 'titolo', 'stato'] },
  { key: 'preventivi', label: 'Preventivi', icon: FileText, fields: ['numero_preventivo', 'titolo', 'totale_finale'] },
  { key: 'conti', label: 'Conti', icon: Receipt, fields: ['numero_conto', 'oggetto', 'totale_finale'] },
  { key: 'computi', label: 'Computi Metrici', icon: Ruler, fields: ['titolo', 'totale_complessivo'] },
  { key: 'cantieri', label: 'Cantieri', icon: HardHat, fields: ['codice_cantiere', 'nome', 'stato'] },
  { key: 'clienti', label: 'Clienti', icon: Users, fields: ['ragione_sociale', 'piva', 'email'] },
  { key: 'fornitori', label: 'Fornitori', icon: Truck, fields: ['ragione_sociale', 'piva', 'email'] },
];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';
const fmtEur = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

function getFieldValue(item, field) {
  const v = item[field];
  if (v === null || v === undefined || v === '') return '-';
  if (field.includes('total')) return fmtEur(v);
  return String(v);
}

export default function ArchivioPage() {
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('pratiche');
  const [data, setData] = useState({});
  const [counts, setCounts] = useState({ total: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        API.get('/api/archivio'),
        API.get('/api/archivio/counts'),
      ]);
      setData(r1.data || {});
      setCounts(r2.data || { total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (entity, id) => {
    if (!await confirm({ title: 'Ripristinare elemento?', message: 'L\'elemento tornerà visibile e operativo nella sua sezione.', confirmLabel: 'Ripristina', variant: 'success' })) return;
    await API.post(`/api/archivio/${entity}/${id}/restore`);
    load();
  };

  const remove = async (entity, id) => {
    if (!await confirm({ title: 'Eliminazione DEFINITIVA', message: 'Stai per cancellare definitivamente questo elemento dal database. Operazione IRREVERSIBILE.', confirmLabel: 'Elimina definitivamente', variant: 'danger' })) return;
    await API.delete(`/api/archivio/${entity}/${id}`);
    load();
  };

  const currentEntity = ENTITIES.find(e => e.key === activeTab);
  const items = data[activeTab] || [];

  return (
    <div className="animate-fade-in" data-testid="archivio-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <Archive size={28} className="text-primary" />
            Archivio
          </h1>
          <p className="text-slate-500 mt-1">
            {counts.total > 0
              ? `${counts.total} elementi archiviati`
              : 'Nessun elemento archiviato'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-1">
        {ENTITIES.map(ent => {
          const Icon = ent.icon;
          const count = counts[ent.key] || 0;
          const active = activeTab === ent.key;
          return (
            <button
              key={ent.key}
              onClick={() => setActiveTab(ent.key)}
              data-testid={`tab-archivio-${ent.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-t-md text-sm font-bold transition-colors ${active ? 'bg-primary text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
            >
              <Icon size={16} />
              {ent.label}
              {count > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${active ? 'bg-white/30' : 'bg-slate-200'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading && <div className="text-slate-500 text-sm">Caricamento...</div>}

      {!loading && items.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <Archive size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Nessun {currentEntity.label.toLowerCase()} archiviato</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {currentEntity.fields.map(f => (
                  <th key={f} className="text-left px-4 py-3 font-bold text-slate-600 uppercase text-xs tracking-wide">
                    {f.replace(/_/g, ' ')}
                  </th>
                ))}
                <th className="text-left px-4 py-3 font-bold text-slate-600 uppercase text-xs tracking-wide">Archiviato il</th>
                <th className="text-right px-4 py-3 font-bold text-slate-600 uppercase text-xs tracking-wide">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id || it._id} className="border-b border-slate-100 hover:bg-slate-50">
                  {currentEntity.fields.map(f => (
                    <td key={f} className="px-4 py-3">{getFieldValue(it, f)}</td>
                  ))}
                  <td className="px-4 py-3 text-slate-500">{fmtDate(it.archived_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => restore(activeTab, it.id || it._id)}
                      data-testid={`restore-${activeTab}-${it.id || it._id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50 rounded-md transition-colors mr-1"
                      title="Ripristina"
                    >
                      <RotateCcw size={14} />
                      Ripristina
                    </button>
                    <button
                      onClick={() => remove(activeTab, it.id || it._id)}
                      data-testid={`delete-${activeTab}-${it.id || it._id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-50 rounded-md transition-colors"
                      title="Elimina definitivamente"
                    >
                      <Trash2 size={14} />
                      Elimina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
